/**
 * Wars: declared for a reason, won or lost on points, and ended on terms.
 *
 * The board this replaced had a war matrix and nothing else. Every crown started
 * at war with every other crown, no war could be entered or left, and the only
 * end condition was annihilation. That is why the map read as a brawl: there was
 * no state called peace for it to return to.
 *
 * Three things live here.
 *
 * **Declaring.** A war needs a defender, and it needs a reason — a casus belli
 * read off the board rather than manufactured on a timer. Declaring without one
 * is allowed and is expensive, which is this world's version of EU4's stability
 * hit.
 *
 * **War score.** One number per war, from the primary attacker's side, moved by
 * battles, by ground taken, and by time. It is *stored*, not derived, and it is
 * moved by events: a battle adds to it when the battle is fought, a province
 * adds to it when the province falls. That keeps the per-turn cost linear in what
 * actually happened rather than in the size of the map.
 *
 * **Peace.** One demand per peace, priced in war score. A truce comes with it,
 * scaled to how hard the peace was, and the truce is what stops the war simply
 * restarting the moment somebody has an army again.
 *
 * ── What is deliberately not here ──────────────────────────────────────────
 *
 * **Occupation.** EU4 separates holding a province from owning it: you occupy in
 * war, and the peace decides who keeps what. This game's map does not have that
 * layer and is not getting one — a province changes hands the moment it is taken,
 * because that is what `captureProvince` has always meant and because a second
 * ownership concept would touch income, the AI's target choice and the renderer,
 * all of which belong to other people this week. So: taking ground *is* the
 * taking, war score measures the course of the war, and a peace settles what
 * *else* is owed — ground the loser still holds, gold, or their independence. A
 * white peace means "we stop; you keep what you took", which is a perfectly good
 * grim outcome and makes the decision to start a war a real one.
 */

import { provinceDef } from '../../data/provinces.ts';
import { neighbours } from './graph.ts';
import {
  addOffer,
  addOpinion,
  addOpinionBoth,
  addOpinionRealm,
  alliesOf,
  hasMarriage,
  isAllied,
  liegeOf,
  opinion,
  bordersNation,
  provinceWorth,
  removeTreaty,
  sameConfession,
  sameRealm,
  setSubject,
  setTruce,
  subjectsOf,
  truceBetween,
  truceTurnsLeft,
} from './diplomacy.ts';
import {
  CasusBelli,
  CB_NAMES,
  COST_CEDE_MAX,
  COST_CEDE_MIN,
  COST_CONCESSIONS,
  COST_UNION,
  COST_VASSALISE,
  COST_WHITE,
  CLAIM_MARRIAGE_TURNS,
  GOLD_PER_SCORE,
  OfferKind,
  OFFER_TURNS,
  OP_ABANDONED_REALM,
  OP_ANSWERED_CALL,
  OP_BROKE_ALLIANCE,
  OP_DECLARED_WAR,
  OP_HUMBLED,
  OP_NO_CB_REALM,
  OP_REFUSED_CALL,
  OP_TOOK_LAND_REALM,
  PeaceTerm,
  SCORE_BATTLE_BASE,
  SCORE_BATTLE_CAP,
  SCORE_BATTLE_PER_POINT,
  SCORE_PROVINCE_BASE,
  SCORE_PROVINCE_CAP,
  SubjectKind,
  TERM_NAMES,
  TreatyKind,
  TRIBUTE_MAX_SHARE,
  TRUCE_BASE,
  TRUCE_MAX,
  TRUCE_PER_COST,
  WAR_SCORE_MAX,
  WAR_WEARY_AFTER,
  WAR_WEARY_DECAY,
  type War,
} from './diplotypes.ts';
import { removePassage } from './diplopass.ts';
import { garrisonCap, logLine, nationName, provinceName } from './state.ts';
import { ArmyStance, CONQUEST_UNREST, type CampaignState } from './types.ts';

// ── Reading the war list ───────────────────────────────────────────────────

export function warById(c: CampaignState, id: number): War | undefined {
  return c.diplo.wars.find((x) => x.id === id);
}

/** The war two crowns are fighting each other in, if any. There is at most one. */
export function warBetween(c: CampaignState, a: number, b: number): War | undefined {
  return c.diplo.wars.find(
    (x) =>
      (x.attackers.includes(a) && x.defenders.includes(b)) ||
      (x.attackers.includes(b) && x.defenders.includes(a)),
  );
}

export function warsOf(c: CampaignState, n: number): War[] {
  return c.diplo.wars.filter((x) => x.attackers.includes(n) || x.defenders.includes(n));
}

export function isInWar(war: War, n: number): boolean {
  return war.attackers.includes(n) || war.defenders.includes(n);
}

/** 1 for the attacking side, -1 for the defending side, 0 for a bystander. */
export function sideOf(war: War, n: number): number {
  if (war.attackers.includes(n)) return 1;
  if (war.defenders.includes(n)) return -1;
  return 0;
}

/** The war score as this crown sees it: positive means it is winning. */
export function scoreFor(war: War, n: number): number {
  return war.score * sideOf(war, n);
}

/** The crown that may actually sign for this side: the one that declared or was declared on. */
export function primaryFor(war: War, n: number): number {
  return sideOf(war, n) >= 0 ? war.attacker : war.defender;
}

export function enemiesIn(war: War, n: number): number[] {
  return sideOf(war, n) === 1 ? war.defenders.slice() : war.attackers.slice();
}

/**
 * Rebuild `Nation.atWar` from the war list, all of it, every time.
 *
 * `atWar` is not a second source of truth — it is the O(1) index over
 * `diplo.wars`, and this is the only function that writes it. Everything hot in
 * the campaign asks "may I shoot at them" (the contact scan, supply, the AI's
 * threat arithmetic, march legality), and answering that by walking a list of
 * wars would put a linear scan inside three nested loops. The index is hashed and
 * saved like everything else; keeping it exactly derived, by recomputing all of
 * it whenever any war changes, is what stops the two from ever disagreeing.
 */
export function refreshWarFlags(c: CampaignState): void {
  for (let i = 0; i < c.nations.length; i++) {
    const row = c.nations[i].atWar;
    for (let j = 0; j < row.length; j++) row[j] = 0;
  }
  for (const war of c.diplo.wars) {
    for (const a of war.attackers) {
      for (const d of war.defenders) {
        if (a === d) continue;
        if (c.nations[a]) c.nations[a].atWar[d] = 1;
        if (c.nations[d]) c.nations[d].atWar[a] = 1;
      }
    }
  }
}

// ── Why you may declare ────────────────────────────────────────────────────

/**
 * Every reason this crown could give for a war on that one, best first.
 *
 * Always non-empty: `CasusBelli.None` is the last entry and is always available,
 * because a crown that wants a war has never in history been stopped by not
 * having a reason. What the pretext buys is the realm's opinion of you
 * afterwards, and what a *specific* pretext buys is the right to demand something
 * a bare conquest could not.
 */
export function casusBelliAgainst(c: CampaignState, a: number, b: number): number[] {
  const out: number[] = [];
  if (a === b || a < 0 || b < 0) return out;
  if (!c.nations[a]?.alive || !c.nations[b]?.alive) return out;

  // Ground of yours that they are standing on.
  const reconquest = c.provinces.some((p) => p.owner === b && p.claimedBy === a);
  if (reconquest) out.push(CasusBelli.Reconquest);

  // A marriage that has stood long enough to be an argument about succession.
  const marriage = c.diplo.treaties.find(
    (t) =>
      t.kind === TreatyKind.RoyalMarriage &&
      ((t.a === a && t.b === b) || (t.a === b && t.b === a)),
  );
  if (marriage && c.turn - marriage.since >= CLAIM_MARRIAGE_TURNS) out.push(CasusBelli.Claim);

  if (!sameConfession(c, a, b)) out.push(CasusBelli.HolyWar);

  // Somebody else's subject, which you would rather was yours.
  if (liegeOf(c, b) >= 0 && liegeOf(c, b) !== a) out.push(CasusBelli.Subjugation);

  if (bordersFor(c, a, b)) out.push(CasusBelli.Conquest);

  out.push(CasusBelli.None);
  return out;
}

/**
 * A pretext of conquest needs a shared border — and a lane is a border.
 *
 * Delegated to `bordersNation` rather than kept as a second copy of the same
 * walk. The two were identical and drifted the moment the sea existed: this one
 * still read the road graph alone, so a crown across a channel was a crown you
 * had no reason to declare on even once it had become a crown you *could*
 * declare on. See the long note on `bordersNation`.
 */
function bordersFor(c: CampaignState, a: number, b: number): boolean {
  return bordersNation(c, a, b);
}

/**
 * Why this war may not be declared, in words, or the empty string if it may.
 *
 * Returns a sentence rather than a boolean because every one of these is
 * something the world map has to be able to put under a greyed-out button. A
 * refusal the player cannot read is a bug report.
 */
export function declareWarReason(c: CampaignState, a: number, b: number): string {
  if (a === b || a < 0 || b < 0) return 'There is nobody there.';
  if (!c.nations[a]?.alive) return 'You no longer hold anything.';
  if (!c.nations[b]?.alive) return `${nationName(c, b)} is already finished.`;
  if (warBetween(c, a, b)) return `You are already at war with ${nationName(c, b)}.`;
  const truce = truceTurnsLeft(c, a, b);
  if (truce > 0) return `A truce with ${nationName(c, b)} stands for ${truce} more turns.`;
  if (isAllied(c, a, b)) return `${nationName(c, b)} is your ally.`;
  if (sameRealm(c, a, b)) return 'They are of your own realm.';
  if (liegeOf(c, a) >= 0) return `${nationName(c, liegeOf(c, a))} decides your wars.`;
  return '';
}

export function canDeclareWar(c: CampaignState, a: number, b: number): boolean {
  return declareWarReason(c, a, b) === '';
}

// ── Declaring ──────────────────────────────────────────────────────────────

/**
 * Start a war, and call in everybody who owes somebody something.
 *
 * The order matters and is the reason this is one function rather than three.
 * The war exists first, then subjects are folded in on both sides without being
 * asked — a vassal has no say, that is what being a vassal is — and only then are
 * free allies called, because a call to arms has to be able to see the war it is
 * being asked to join.
 *
 * Nothing here rolls. An AI ally's answer is a deterministic reading of what it
 * thinks of the caller and what it already has on its hands, so a player can
 * learn it. The player's own answer is an offer, and waits.
 */
export function declareWar(
  c: CampaignState,
  attacker: number,
  defender: number,
  cb: number,
  goal = -1,
): War | null {
  if (!canDeclareWar(c, attacker, defender)) return null;

  const war: War = {
    id: c.diplo.nextWarId++,
    attacker,
    defender,
    attackers: [attacker],
    defenders: [defender],
    cb,
    goal,
    startTurn: c.turn,
    score: 0,
    battlesWon: 0,
    battlesLost: 0,
    taken: 0,
    lost: 0,
    lastOffer: -99,
  };
  c.diplo.wars.push(war);

  /**
   * Any leave to cross one another's ground ends here, in both directions.
   *
   * Not merely tidiness. War itself grants passage against the crown you are
   * fighting, so a grant that survived the declaration would be invisible for
   * the whole war and then *reappear* at the peace — handing the winner a
   * standing right to keep an army in the loser's country, granted before there
   * was a quarrel and never revisited by anybody. A crown that wants that back
   * has to ask for it again, at the table or afterwards.
   */
  removePassage(c, attacker, defender);
  removePassage(c, defender, attacker);

  for (const s of subjectsOf(c, attacker)) enlist(c, war, s, 1);
  for (const s of subjectsOf(c, defender)) enlist(c, war, s, -1);
  refreshWarFlags(c);

  addOpinionBoth(c, attacker, defender, OP_DECLARED_WAR);
  if (cb === CasusBelli.None) {
    addOpinionRealm(c, attacker, OP_NO_CB_REALM, [attacker]);
  }

  logLine(
    c,
    `${nationName(c, attacker)} declares war on ${nationName(c, defender)} — ` +
      `${CB_NAMES[cb] ?? 'no pretext'}.`,
  );

  // Free allies are asked. The defender's first, because being called to defend
  // is the case the player will meet most often and the chronicle should read in
  // the order the heralds actually rode.
  callAllies(c, war, defender, -1);
  callAllies(c, war, attacker, 1);
  refreshWarFlags(c);
  return war;
}

/** Put a crown on a side without asking it. Subjects and answered calls both. */
function enlist(c: CampaignState, war: War, n: number, side: number): void {
  if (isInWar(war, n)) return;
  if (!c.nations[n]?.alive) return;
  if (side >= 0) war.attackers.push(n);
  else war.defenders.push(n);
}

export function joinWar(c: CampaignState, war: War, n: number, side: number): void {
  enlist(c, war, n, side);
  for (const s of subjectsOf(c, n)) enlist(c, war, s, side);
  refreshWarFlags(c);
}

/**
 * Ring the bell for one side's allies.
 *
 * An AI ally answers here and now. The player is handed an offer, which is the
 * whole reason offers exist: the map cannot wait for a decision, so the decision
 * becomes state and the war carries on without them until they make it.
 */
export function callAllies(c: CampaignState, war: War, caller: number, side: number): void {
  for (const ally of alliesOf(c, caller)) {
    if (isInWar(war, ally)) continue;
    if (ally === c.playerNation) {
      const posted = addOffer(c, {
        kind: OfferKind.CallToArms,
        from: caller,
        to: ally,
        war: war.id,
        term: -1,
        province: -1,
        gold: 0,
        fromConcedes: false,
        expires: c.turn + OFFER_TURNS,
      });
      if (posted) {
        logLine(
          c,
          `${nationName(c, caller)} calls you to arms against ` +
            `${nationName(c, enemiesIn(war, caller)[0] ?? -1)}.`,
        );
      }
      continue;
    }
    if (allyWillCome(c, war, caller, ally)) {
      joinWar(c, war, ally, side);
      addOpinion(c, caller, ally, OP_ANSWERED_CALL);
      logLine(c, `${nationName(c, ally)} answers ${nationName(c, caller)}'s call.`);
    } else {
      refuseCall(c, caller, ally);
    }
  }
}

/**
 * Whether an AI ally comes when it is called.
 *
 * Deterministic, and stated in three clauses so a player can hold it in their
 * head: it will not come if it dislikes you, it will not come if it is already
 * fighting somebody, and it cannot come if it has a truce with the crown you
 * want it to fight. Everything else, it comes — an alliance that is a coin flip
 * is not an alliance.
 */
export function allyWillCome(c: CampaignState, war: War, caller: number, ally: number): boolean {
  if (opinion(c, ally, caller) < 20) return false;
  if (warsOf(c, ally).length > 0) return false;
  for (const enemy of enemiesIn(war, caller)) {
    if (truceBetween(c, ally, enemy)) return false;
  }
  return true;
}

/** What it costs to leave an ally standing in the road. */
export function refuseCall(c: CampaignState, caller: number, refuser: number): void {
  removeTreaty(c, TreatyKind.Alliance, caller, refuser);
  addOpinion(c, caller, refuser, OP_REFUSED_CALL);
  addOpinionRealm(c, refuser, OP_ABANDONED_REALM, [refuser]);
  logLine(
    c,
    `${nationName(c, refuser)} does not answer ${nationName(c, caller)}. ` +
      `The alliance is over, and the realm is watching.`,
  );
}

// ── War score ──────────────────────────────────────────────────────────────

function bump(war: War, delta: number, side: number): void {
  const next = Math.max(-WAR_SCORE_MAX, Math.min(WAR_SCORE_MAX, war.score + delta * side));
  // Never store negative zero: it hashes differently from zero and a save
  // round-trips it as zero. See `clampOpinion` in `diplomacy.ts` for the full
  // story, which cost an afternoon the first time.
  war.score = next === 0 ? 0 : next;
}

/**
 * A battle went somebody's way. Called from `conquest.ts`, on both resolution
 * paths, so a fight settled by hand and a fight settled by arithmetic move the
 * ledger identically.
 */
export function recordBattleScore(
  c: CampaignState,
  winner: number,
  loser: number,
  loserPoints: number,
): void {
  if (winner < 0 || loser < 0) return;
  const war = warBetween(c, winner, loser);
  if (!war) return;
  const side = sideOf(war, winner);
  if (side === 0) return;
  const worth = Math.min(
    SCORE_BATTLE_CAP,
    SCORE_BATTLE_BASE + Math.max(0, loserPoints) * SCORE_BATTLE_PER_POINT,
  );
  bump(war, worth, side);
  if (side === 1) war.battlesWon++;
  else war.battlesLost++;
}

/** Ground changed hands. The single largest contribution to a war score. */
export function recordCaptureScore(
  c: CampaignState,
  taker: number,
  formerOwner: number,
  province: number,
): void {
  if (taker < 0 || formerOwner < 0) return;
  const war = warBetween(c, taker, formerOwner);
  if (!war) return;
  const side = sideOf(war, taker);
  if (side === 0) return;
  const def = provinceDef(province);
  const worth = Math.min(
    SCORE_PROVINCE_CAP,
    SCORE_PROVINCE_BASE + def.wealth * 0.5 + c.provinces[province].fort * 3,
  );
  bump(war, worth, side);
  if (side === 1) war.taken++;
  else war.lost++;
}

/**
 * Long wars forget.
 *
 * Score bleeds toward zero rather than toward either side. A war that has run
 * eighteen turns without either crown finishing it is a war neither of them is
 * winning, and pushing it toward zero pushes it toward a white peace — which is
 * the outcome two exhausted powers should reach. Bleeding it toward the defender,
 * as EU4 does through war exhaustion, would hand a win to whoever stood still.
 */
export function warWeariness(c: CampaignState): void {
  for (const war of c.diplo.wars) {
    if (c.turn - war.startTurn < WAR_WEARY_AFTER) continue;
    if (war.score > 0) war.score = Math.max(0, war.score - WAR_WEARY_DECAY);
    else if (war.score < 0) {
      const next = Math.min(0, war.score + WAR_WEARY_DECAY);
      war.score = next === 0 ? 0 : next;
    }
  }
}

// ── Peace ──────────────────────────────────────────────────────────────────

/** What one demand costs, in war score. -1 when the demand is not available. */
export function termCost(
  c: CampaignState,
  war: War,
  taker: number,
  term: number,
  province = -1,
): number {
  const side = sideOf(war, taker);
  if (side === 0) return -1;
  const loser = side === 1 ? war.defender : war.attacker;
  switch (term) {
    case PeaceTerm.White:
      return COST_WHITE;
    case PeaceTerm.Cede: {
      if (province < 0 || province >= c.provinces.length) return -1;
      if (c.provinces[province].owner !== loser) return -1;
      const worth = provinceWorth(c, province);
      return Math.round(Math.max(COST_CEDE_MIN, Math.min(COST_CEDE_MAX, COST_CEDE_MIN + worth * 1.3)));
    }
    case PeaceTerm.Tribute:
      return c.nations[loser].treasury.gold > 40 ? 15 : -1;
    case PeaceTerm.Concessions:
      return COST_CONCESSIONS;
    case PeaceTerm.Vassalise:
      // You cannot swear a crown that already answers to somebody, and you cannot
      // swear one that is bigger than the argument you won.
      return c.nations[loser].liege >= 0 ? -1 : COST_VASSALISE;
    case PeaceTerm.Union:
      if (c.nations[loser].liege >= 0) return -1;
      if (war.cb !== CasusBelli.Claim) return -1;
      if (!hasMarriage(c, taker, loser)) return -1;
      return COST_UNION;
    default:
      return -1;
  }
}

export interface PeaceOption {
  term: number;
  /** Province for a `Cede`, else -1. */
  province: number;
  /** Gold for a `Tribute`, else 0. */
  gold: number;
  cost: number;
  label: string;
}

/**
 * Everything this side could demand right now, cheapest first.
 *
 * Built for the peace panel, and used by the AI to choose — so the player and
 * the council are looking at exactly the same list, which is the only way an AI
 * peace can ever read as fair.
 */
export function availableTerms(c: CampaignState, war: War, taker: number): PeaceOption[] {
  const side = sideOf(war, taker);
  if (side === 0) return [];
  const have = Math.max(0, scoreFor(war, taker));
  const loser = side === 1 ? war.defender : war.attacker;
  const out: PeaceOption[] = [];

  out.push({ term: PeaceTerm.White, province: -1, gold: 0, cost: 0, label: TERM_NAMES[PeaceTerm.White] });

  // Only ground that touches the taker's own, or the war goal. A peace that hands
  // over a province on the far side of the map is a peace nobody can defend, and
  // it is how a strategic map turns into confetti.
  for (const p of cedableProvinces(c, war, taker, loser)) {
    const cost = termCost(c, war, taker, PeaceTerm.Cede, p);
    if (cost < 0 || cost > have) continue;
    out.push({
      term: PeaceTerm.Cede,
      province: p,
      gold: 0,
      cost,
      label: `Cede ${provinceName(p)}`,
    });
  }

  const tributeCost = termCost(c, war, taker, PeaceTerm.Tribute);
  if (tributeCost >= 0 && have >= tributeCost) {
    const gold = tributeGold(c, war, taker, have);
    if (gold > 0) {
      out.push({
        term: PeaceTerm.Tribute,
        province: -1,
        gold,
        cost: Math.min(have, Math.ceil(gold / GOLD_PER_SCORE)),
        label: `${gold} gold in tribute`,
      });
    }
  }

  for (const term of [PeaceTerm.Concessions, PeaceTerm.Vassalise, PeaceTerm.Union]) {
    const cost = termCost(c, war, taker, term);
    if (cost < 0 || cost > have) continue;
    out.push({ term, province: -1, gold: 0, cost, label: TERM_NAMES[term] });
  }

  out.sort((a, b) => a.cost - b.cost || a.term - b.term || a.province - b.province);
  return out;
}

/** Ground the loser holds that the winner could actually hold in turn. */
export function cedableProvinces(
  c: CampaignState,
  war: War,
  taker: number,
  loser: number,
): number[] {
  const out: number[] = [];
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== loser) continue;
    if (p === war.goal) {
      out.push(p);
      continue;
    }
    const touches = neighbours(c, p).some(
      (q) => c.provinces[q].owner === taker || c.provinces[q].claimedBy === taker,
    );
    if (touches) out.push(p);
  }
  return out;
}

/** How much gold a given war score is actually worth, against what they have. */
export function tributeGold(c: CampaignState, war: War, taker: number, score: number): number {
  const side = sideOf(war, taker);
  if (side === 0) return 0;
  const loser = side === 1 ? war.defender : war.attacker;
  const purse = c.nations[loser].treasury.gold;
  return Math.floor(Math.min(purse * TRIBUTE_MAX_SHARE, Math.max(0, score) * GOLD_PER_SCORE));
}

/**
 * Sign it. One demand, paid for out of war score, and a truce on the end of it.
 *
 * `taker` is the crown cashing in — it must be a primary, because a peace is
 * signed by the crowns that started the thing, and letting a called-in ally sign
 * away somebody else's war is how a diplomacy system starts producing outcomes
 * nobody can explain.
 */
export function makePeace(
  c: CampaignState,
  war: War,
  taker: number,
  term: number,
  province = -1,
): boolean {
  const side = sideOf(war, taker);
  if (side === 0) return false;
  if (taker !== war.attacker && taker !== war.defender) return false;
  const loser = side === 1 ? war.defender : war.attacker;
  const cost = termCost(c, war, taker, term, province);
  if (cost < 0) return false;
  if (cost > Math.max(0, scoreFor(war, taker))) return false;

  const takerName = nationName(c, taker);
  const loserName = nationName(c, loser);

  switch (term) {
    case PeaceTerm.White:
      logLine(c, `${takerName} and ${loserName} put down their arms. Nothing is owed either way.`);
      break;

    case PeaceTerm.Cede: {
      cedeProvince(c, province, taker, loser);
      logLine(c, `${loserName} signs ${provinceName(province)} over to ${takerName}.`);
      // The realm notices land changing hands at the point of a sword. This is
      // the whole of the aggressive-expansion model, and it is why a crown that
      // wins three wars in a row finds nobody willing to ally with it.
      addOpinionRealm(c, taker, OP_TOOK_LAND_REALM, [taker, loser]);
      break;
    }

    case PeaceTerm.Tribute: {
      const gold = tributeGold(c, war, taker, scoreFor(war, taker));
      c.nations[loser].treasury.gold = Math.max(0, c.nations[loser].treasury.gold - gold);
      c.nations[taker].treasury.gold += gold;
      logLine(c, `${loserName} buys peace from ${takerName} with ${gold} gold.`);
      break;
    }

    case PeaceTerm.Concessions: {
      for (const ally of alliesOf(c, loser)) removeTreaty(c, TreatyKind.Alliance, loser, ally);
      addOpinionRealm(c, loser, OP_HUMBLED, [loser]);
      logLine(
        c,
        `${loserName} renounces its alliances at ${takerName}'s insistence. It stands alone now.`,
      );
      break;
    }

    case PeaceTerm.Vassalise:
      setSubject(c, loser, taker, SubjectKind.Vassal);
      addOpinionRealm(c, taker, OP_TOOK_LAND_REALM, [taker, loser]);
      logLine(c, `${loserName} bends the knee to ${takerName}.`);
      break;

    case PeaceTerm.Union:
      setSubject(c, loser, taker, SubjectKind.Union);
      addOpinionRealm(c, taker, OP_TOOK_LAND_REALM, [taker, loser]);
      logLine(
        c,
        `The claim is pressed and granted: ${loserName} and ${takerName} are two crowns on one head.`,
      );
      break;

    default:
      return false;
  }

  endWar(c, war, truceLength(cost));
  return true;
}

/** A harder peace buys a longer truce, up to a cap the map can plan around. */
export function truceLength(cost: number): number {
  return Math.round(Math.min(TRUCE_MAX, TRUCE_BASE + Math.max(0, cost) * TRUCE_PER_COST));
}

/**
 * Take the war off the board and put a truce between everybody who was in it.
 *
 * Every pair, not just the primaries: an ally that was called in and then peaced
 * out by somebody else's signature has still just fought a war, and letting it
 * re-declare on the same crown the following turn would make the truce
 * meaningless for exactly the nations most likely to want one.
 */
export function endWar(c: CampaignState, war: War, truceTurns: number): void {
  for (const a of war.attackers) {
    for (const d of war.defenders) {
      if (a === d) continue;
      setTruce(c, a, d, truceTurns);
    }
  }
  const i = c.diplo.wars.findIndex((x) => x.id === war.id);
  if (i >= 0) c.diplo.wars.splice(i, 1);
  /**
   * Nobody is besieging anything on behalf of a war that no longer exists.
   *
   * `a.stance` is cleared as well as `siegeTurns`, and it was not — which was a
   * live bug rather than an oversight this change happened to notice. `doSieges`
   * walks every host whose stance is `Besieging` and does not ask whether there
   * is a war on: it grinds the garrison down twelve a month and opens the gates
   * on schedule. So a peace signed under a besieging army zeroed its clock,
   * left it sitting, and it took the province anyway a few months later —
   * capturing ground from a crown it was at peace with, with no war, no casus
   * belli and no line in the chronicle to explain it.
   */
  for (const a of c.armies) {
    if (!isInWar(war, a.owner)) continue;
    const prov = c.provinces[a.at];
    if (prov.owner >= 0 && prov.owner !== a.owner && !isInWar(war, prov.owner)) continue;
    if (prov.siegeBy === a.owner) {
      prov.siegeBy = -1;
      prov.siegeT = 0;
    }
    a.siegeTurns = 0;
    a.stance = ArmyStance.Field;
  }
  refreshWarFlags(c);
}

/**
 * A province handed over rather than stormed.
 *
 * Deliberately not `captureProvince`. That function models a wall being carried:
 * it knocks a level off the fortification and leaves a quarter garrison, because
 * somebody has just been through it. A ceded province was signed away intact, so
 * the walls stand and the garrison changes livery — and it is *more* restive for
 * it, because the people in it were not conquered, they were traded.
 */
export function cedeProvince(
  c: CampaignState,
  province: number,
  to: number,
  from: number,
): void {
  const prov = c.provinces[province];
  prov.claimedBy = from;
  prov.owner = to;
  prov.garrison = Math.round(garrisonCap(c, province) * 0.5);
  prov.unrest = Math.min(1, CONQUEST_UNREST + 0.15);
  prov.siegeBy = -1;
  prov.siegeT = 0;
}

// ── Housekeeping the turn does ─────────────────────────────────────────────

/**
 * Wars whose principals are gone, and sides that have emptied out.
 *
 * A crown can be eliminated in the middle of a war it started. `checkElimination`
 * lives in `state.ts` and knows nothing about any of this on purpose — the two
 * would otherwise import each other — so the war list is swept once a turn
 * instead. A war with nobody left on one side is over, and it ends without a
 * truce, because there is nobody to hold one.
 */
export function reapWars(c: CampaignState): void {
  const dead: War[] = [];
  for (const war of c.diplo.wars) {
    war.attackers = war.attackers.filter((n) => c.nations[n]?.alive);
    war.defenders = war.defenders.filter((n) => c.nations[n]?.alive);
    if (
      war.attackers.length === 0 ||
      war.defenders.length === 0 ||
      !c.nations[war.attacker]?.alive ||
      !c.nations[war.defender]?.alive
    ) {
      dead.push(war);
    }
  }
  for (const war of dead) {
    const i = c.diplo.wars.findIndex((x) => x.id === war.id);
    if (i >= 0) c.diplo.wars.splice(i, 1);
  }
  if (dead.length > 0) refreshWarFlags(c);
}

/** The opinion cost of breaking an alliance on purpose, applied by the command. */
export function breakAlliancePenalty(c: CampaignState, breaker: number, other: number): void {
  addOpinion(c, other, breaker, OP_BROKE_ALLIANCE);
  addOpinionRealm(c, breaker, Math.round(OP_BROKE_ALLIANCE / 4), [breaker]);
}
